import {
  AutoComplete,
  AutoCompleteCreatable,
  AutoCompleteInput,
  AutoCompleteItem,
  AutoCompleteList,
  AutoCompleteTag,
} from '@choc-ui/chakra-autocomplete';
import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/react';
import useTranslate from '~/common/utils/translate';
import { useField } from 'formik';

type TagCompleteProps = {
  name: string;
  label?: string;
  options: string[];
  placeholder?: string;
  isDisabled?: boolean;
};

export function TagComplete({
  name,
  label,
  options,
  placeholder,
  isDisabled,
}: TagCompleteProps) {
  const t = useTranslate();
  const [field, meta, helpers] = useField<string[]>(name);

  return (
    <FormControl isDisabled={isDisabled} isInvalid={!!meta.error}>
      <FormLabel>{t(label)}</FormLabel>
      <AutoComplete
        rollNavigation
        openOnFocus
        creatable
        multiple
        suggestWhenEmpty
        focusInputOnSelect
        filter={(query: string, _value: string, label: string) =>
          label.toLowerCase().includes(query.toLowerCase())
        }
        value={field.value}
        onChange={(value) => void helpers.setValue(value as string[])}
      >
        <Box
          sx={{
            borderColor: '#807a7a',
            borderWidth: '1px',
            p: '1px',
            borderRadius: 'md',
            '&:hover': {
              borderColor: 'gray.300',
            },
            '&:focus-within': {
              borderColor: 'green.600',
              borderWidth: '2px',
              p: '0px',
            },
          }}
        >
          <AutoCompleteInput
            variant="filled"
            sx={{
              bg: 'inherit',
              '&:hover': {
                bg: 'inherit',
              },
            }}
            placeholder={t(placeholder)}
          >
            {({ tags }) =>
              tags.map((tag, tid) => (
                <AutoCompleteTag
                  key={tid}
                  label={tag.label as string}
                  onRemove={tag.onRemove}
                />
              ))
            }
          </AutoCompleteInput>
        </Box>
        <AutoCompleteList>
          {options.map((option, index) => (
            <AutoCompleteItem
              key={`tag-complete-option-${index}`}
              value={option}
            >
              {option}
            </AutoCompleteItem>
          ))}
          <AutoCompleteCreatable />
        </AutoCompleteList>
      </AutoComplete>
      {!!meta.error && <FormErrorMessage>{t(meta.error)}</FormErrorMessage>}
    </FormControl>
  );
}
